import { get } from "radash"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"

import type { GeocodeV4Response } from "@be-miniapp/api"
import type { GeocodeV4Address, Location } from "@be-miniapp/types"

import { useUserStore } from "@/store/user-session"

export interface FoodLocation {
  name?: string
  address?: string
  placeId?: string
  location?: Location
  distance?: string
}

interface FoodSessionData {
  isEnabled: boolean

  customLocation?: FoodLocation | undefined
  lastSuccessLocation?: FoodLocation | undefined

  isLocationManuallySet?: boolean
  cartId?: number
}

interface FoodSessionDataActions {
  setCustomLocation: (
    customLocation: FoodLocation | undefined,
    isLocationManuallySet?: boolean
  ) => void
  fromGeocodeLocation: (geocodeLocation: GeocodeV4Response) => void
  setLastSuccessLocation: (
    lastSuccessLocation: FoodLocation | undefined
  ) => void
  resetCustomLocation: () => void
  setCartId: (cartId: number) => void
  setEnabled: (isEnabled: boolean) => void
}
const fromGeocodeLocationToFoodLocation = (
  geocodeLocation: GeocodeV4Response
) => {
  const firstLocation = get(
    geocodeLocation,
    "address_full.results[0]"
  ) as GeocodeV4Address

  const name = get(
    geocodeLocation,
    "polygon.structured_formatting.main_text",
    firstLocation?.formatted_address.split(",")[0]
  )
  const address = get(
    geocodeLocation,
    "polygon.structured_formatting.secondary_text",
    firstLocation?.formatted_address
  )

  return {
    name: name as string,
    address: address as string,
    placeId: firstLocation?.place_id,
    location: {
      latitude: firstLocation?.geometry.location.lat ?? 0,
      longitude: firstLocation?.geometry.location.lng ?? 0,
    },
  }
}

const initLoc =
  useUserStore.getState().currentAddress &&
  fromGeocodeLocationToFoodLocation(useUserStore.getState().currentAddress!)

const initialState: FoodSessionData = {
  isEnabled: false,
  customLocation: {
    ...(initLoc ? initLoc : {}),
  },
  cartId: 0,
}

type FoodSessionDataStore = FoodSessionData & FoodSessionDataActions

export const useFoodSessionData = create(
  devtools(
    immer<FoodSessionDataStore>((set, _get) => ({
      ...initialState,
      setCustomLocation: (
        customLocation: FoodLocation | undefined,
        isLocationManuallySet: boolean = false
      ) => {
        if (customLocation?.placeId === _get().customLocation?.placeId) return

        set((v) => {
          v.customLocation = customLocation
          v.isLocationManuallySet = isLocationManuallySet
        })
      },
      setLastSuccessLocation: (
        lastSuccessLocation: FoodLocation | undefined
      ) => {
        set({
          lastSuccessLocation,
        })
      },
      fromGeocodeLocation: (
        geocodeLocation: GeocodeV4Response,
        isLocationManuallySet: boolean = false
      ) => {
        set({
          customLocation: fromGeocodeLocationToFoodLocation(geocodeLocation),

          isLocationManuallySet,
        })
      },
      resetCustomLocation: () => {
        set({
          customLocation: initialState.customLocation,
          lastSuccessLocation: initialState.customLocation,
          isLocationManuallySet: false,
        })
      },
      setCartId: (cartId: number) => set({ cartId }),
      setEnabled: (isEnabled: boolean) => set({ isEnabled }),
    }))
  )
)
